/**
 * 통합 계좌 설정 서비스
 */
import api from './api';

class ConfigService {
  constructor() {
    this.config = null;
    this.accounts = [];
    this.brokers = {};
    this.isLoaded = false;
  }

  /**
   * 통합 설정 로드
   */
  async loadConfig() {
    try {
      // console.log('🔄 통합 계좌 설정 로드 중...');
      
      const response = await api.get('/api/data_predator/config/accounts/');
      
      if (response.data && response.data.success) {
        this.config = response.data.data;
        this.accounts = this._parseAccounts(this.config);
        this.brokers = this.config.brokers || {};
        this.isLoaded = true;
        
        // console.log('✅ 통합 계좌 설정 로드 성공:', {
        //   accounts: this.accounts.length,
        //   brokers: Object.keys(this.brokers).length
        // });
        
        return this.config;
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('❌ 통합 계좌 설정 로드 실패:', error);
      
      // 폴백: 기본 설정 사용
      this._loadFallbackConfig();
      throw error;
    }
  }

  /**
   * 계좌 정보 파싱
   */
  _parseAccounts(config) {
    const accounts = [];
    
    if (!config || !config.accounts || !config.brokers) {
      return accounts;
    }

    for (const account of config.accounts) {
      if (account.active) {
        const brokerConfig = config.brokers[account.broker];
        if (brokerConfig) {
          accounts.push({
            accountId: account.account_id,
            broker: account.broker,
            brokerName: brokerConfig.name,
            accountNumber: account.account_number,
            accountName: account.account_name,
            description: account.description,
            color: brokerConfig.color,
            tradingEnabled: account.trading_enabled,
            priority: account.priority,
            accountType: account.account_type
          });
        }
      }
    }

    // 우선순위로 정렬
    return accounts.sort((a, b) => a.priority - b.priority);
  }

  /**
   * 폴백 설정 로드 (기본값)
   */
  _loadFallbackConfig() {
    console.warn('⚠️ 폴백 설정 사용');
    
    this.accounts = [
      {
        accountId: 'kis_main',
        broker: 'kis',
        brokerName: '한국투자증권',
        accountNumber: '64798261',
        accountName: 'KIS 계좌',
        description: '기본 KIS 계좌',
        color: '#1890ff',
        tradingEnabled: true,
        priority: 1,
        accountType: 'stock'
      }
    ];
    
    this.brokers = {
      kis: {
        name: '한국투자증권',
        color: '#1890ff'
      }
    };
    
    this.isLoaded = true;
  }

  /**
   * 모든 활성 계좌 반환
   */
  getAllAccounts() {
    return this.accounts;
  }

  /**
   * 특정 브로커의 계좌들 반환
   */
  getAccountsByBroker(broker) {
    return this.accounts.filter(account => account.broker === broker);
  }

  /**
   * 계좌 ID로 계좌 정보 반환
   */
  getAccountById(accountId) {
    return this.accounts.find(account => account.accountId === accountId);
  }

  /**
   * 자동매매 활성화된 계좌들 반환
   */
  getTradingEnabledAccounts() {
    return this.accounts.filter(account => account.tradingEnabled);
  }

  /**
   * 브로커 정보 반환
   */
  getBrokerInfo(brokerName) {
    return this.brokers[brokerName];
  }

  /**
   * 모든 브로커 정보 반환
   */
  getAllBrokers() {
    return this.brokers;
  }

  /**
   * 설정이 로드되었는지 확인
   */
  isConfigLoaded() {
    return this.isLoaded;
  }

  /**
   * 설정 다시 로드
   */
  async reloadConfig() {
    // console.log('🔄 설정 다시 로드 중...');
    this.isLoaded = false;
    this.config = null;
    this.accounts = [];
    this.brokers = {};
    
    return await this.loadConfig();
  }

  /**
   * 여러 계좌 잔고 조회
   */
  async getMultipleAccountBalances(accounts, useCache = true) {
    try {
      // console.log('🔄 다중 계좌 잔고 조회 중...', accounts);
      
      const response = await api.post('/api/data_predator/config/accounts/balances/', {
        accounts: accounts,
        use_cache: useCache
      });
      
      if (response.data && response.data.success) {
        // console.log('✅ 다중 계좌 잔고 조회 성공');
        return response.data;
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('❌ 다중 계좌 잔고 조회 실패:', error);
      throw error;
    }
  }

  /**
   * KIS CMA 금액 또는 NH 수동 가용자금 조회
   */
  async getManualCashBalance(accountId) {
    try {
      const response = await api.get(`/api/data_predator/config/accounts/${accountId}/manual-cash/`);
      return response.data;
    } catch (error) {
      console.error('수동 입력 금액 조회 실패:', error);
      throw error;
    }
  }

  /**
   * KIS CMA 금액 또는 NH 수동 가용자금 저장
   */
  async updateManualCashBalance(accountId, availableBalance) {
    try {
      const response = await api.put(`/api/data_predator/config/accounts/${accountId}/manual-cash/`, {
        available_balance: availableBalance,
        manual_cma_balance: availableBalance
      });
      return response.data;
    } catch (error) {
      console.error('수동 입력 금액 저장 실패:', error);
      throw error;
    }
  }

  /**
   * 설정 상태 확인
   */
  async getConfigHealth() {
    try {
      const response = await api.get('/api/data_predator/config/health/');
      return response.data;
    } catch (error) {
      console.error('설정 상태 확인 실패:', error);
      throw error;
    }
  }

  /**
   * 서버 설정 다시 로드
   */
  async reloadServerConfig() {
    try {
      const response = await api.post('/api/data_predator/config/reload/');
      if (response.data && response.data.success) {
        // 클라이언트 설정도 다시 로드
        await this.reloadConfig();
        return response.data;
      } else {
        throw new Error('Server config reload failed');
      }
    } catch (error) {
      console.error('서버 설정 다시 로드 실패:', error);
      throw error;
    }
  }

  /**
   * 디버그 정보 출력
   */
  debugInfo() {
    return {
      isLoaded: this.isLoaded,
      accountsCount: this.accounts.length,
      brokersCount: Object.keys(this.brokers).length,
      accounts: this.accounts,
      brokers: this.brokers,
      config: this.config
    };
  }
}

// 싱글톤 인스턴스 생성
const configService = new ConfigService();

export default configService;
